import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Inbox, Users, Phone, Mail, FileText, X } from "lucide-react";

const STATUS_CLS = {
  new: "bg-[#F59E0B]/15 text-[#F59E0B]",
  in_review: "bg-[#3B82F6]/15 text-[#93C5FD]",
  quoted: "bg-[#A855F7]/15 text-[#D8B4FE]",
  converted: "bg-[#10B981]/15 text-[#10B981]",
  closed: "bg-[#6B7280]/20 text-[#A8ACB4]",
  lost: "bg-[#EF4444]/15 text-[#FCA5A5]",
};
const SOURCES = [["phone", "Telefon"], ["walk-in", "På plats"], ["website", "Hemsidan"], ["instagram", "Instagram"], ["facebook", "Facebook"], ["sms", "SMS"]];
const EMPTY = { customer_name: "", customer_phone: "", customer_email: "", subject: "", body: "", event_date: "", event_time: "", party_size: "", source: "phone" };

export default function InquiriesPanel({ onConverted }) {
  const [inquiries, setInquiries] = useState([]);
  const [statusDef, setStatusDef] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/inquiries${filter ? `?status=${filter}` : ""}`);
      setInquiries(res.data.inquiries || []);
      setStatusDef(res.data.status_def || null);
    } catch { toast.error("Kunde inte ladda förfrågningar"); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const label = (s) => statusDef?.labels?.[s] || s;

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/inquiries", { ...form, party_size: form.party_size ? parseInt(form.party_size) : undefined });
      toast.success("Förfrågan skapad");
      setDialog(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte skapa förfrågan"); }
    finally { setSaving(false); }
  };

  const setStatus = async (inq, status) => {
    setBusyId(inq.id);
    try { await api.put(`/admin/inquiries/${inq.id}/status`, { status }); toast.success(`Status: ${label(status)}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Misslyckades"); }
    finally { setBusyId(null); }
  };

  const toQuote = async (inq) => {
    setBusyId(inq.id);
    try {
      const res = await api.post(`/admin/inquiries/${inq.id}/convert-to-quote`);
      toast.success(`Offert ${res.data.quote_number} skapad — fyll i rader under Offerter`);
      load(); onConverted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte skapa offert"); }
    finally { setBusyId(null); }
  };

  const finals = statusDef?.final || ["converted", "closed", "lost"];
  const open = inquiries.filter(i => !finals.includes(i.status));
  const done = inquiries.filter(i => finals.includes(i.status));

  const row = (inq) => (
    <div key={inq.id} className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium">
            {inq.customer_name}
            <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[inq.status] || "bg-[#27272A] text-[#C8C8CE]"}`}>{label(inq.status)}</span>
          </p>
          {inq.subject && <p className="text-xs text-[#D4B483] mt-1">{inq.subject}</p>}
          {inq.body && <p className="text-[11px] text-[#C8C8CE] mt-1 line-clamp-2">{inq.body}</p>}
          <p className="text-[11px] text-[#9A9AA2] mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {inq.customer_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{inq.customer_phone}</span>}
            {inq.customer_email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{inq.customer_email}</span>}
            {inq.party_size ? <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{inq.party_size} gäster</span> : null}
            {inq.event_date && <span>{inq.event_date}{inq.event_time ? ` ${inq.event_time}` : ""}</span>}
            <span>via {SOURCES.find(s => s[0] === inq.source)?.[1] || inq.source}</span>
          </p>
        </div>
        {!finals.includes(inq.status) && (
          <div className="flex gap-1.5 shrink-0 flex-wrap">
            {inq.status === "new" && (
              <Button size="sm" variant="outline" disabled={busyId === inq.id} onClick={() => setStatus(inq, "in_review")}
                className="border-[#3B82F6]/40 text-[#93C5FD] hover:bg-[#3B82F6]/10 h-8 text-xs">Hanteras</Button>
            )}
            {["new", "in_review"].includes(inq.status) && (
              <Button size="sm" disabled={busyId === inq.id} onClick={() => toQuote(inq)}
                className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-8 text-xs font-semibold">
                <FileText className="w-3.5 h-3.5 mr-1" /> Skapa offert</Button>
            )}
            <Button size="sm" variant="outline" disabled={busyId === inq.id} onClick={() => setStatus(inq, "closed")}
              className="border-[#6B7280]/40 text-[#A8ACB4] hover:bg-[#6B7280]/10 h-8 text-xs">Avsluta</Button>
            <Button size="sm" variant="outline" disabled={busyId === inq.id} onClick={() => setStatus(inq, "lost")}
              className="border-[#EF4444]/40 text-[#FCA5A5] hover:bg-[#EF4444]/10 h-8 text-xs"><X className="w-3.5 h-3.5" /></Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl" data-testid="inquiries-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Förfrågningar</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">Catering- och eventförfrågningar — första steget innan offert och bokning.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-md h-9 text-sm px-2">
            <option value="">Alla statusar</option>
            {(statusDef?.statuses || Object.keys(STATUS_CLS)).map(s => <option key={s} value={s}>{label(s)}</option>)}
          </select>
          <Button onClick={() => { setForm(EMPTY); setDialog(true); }}
            className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Ny förfrågan
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : inquiries.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <Inbox className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga förfrågningar{filter ? " med den statusen" : " ännu"}.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {open.map(row)}
          {done.length > 0 && (
            <>
              <p className="text-[11px] uppercase tracking-wider text-[#9A9AA2] pt-3">Avslutade</p>
              <div className="opacity-60 space-y-2">{done.map(row)}</div>
            </>
          )}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-md">
          <DialogHeader><DialogTitle className="text-white">Ny förfrågan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Namn *</Label>
                <Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Telefon</Label>
                <Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div className="col-span-2">
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">E-post</Label>
                <Input value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Datum (event)</Label>
                <Input type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Tid</Label>
                <Input type="time" value={form.event_time} onChange={e => setForm(f => ({ ...f, event_time: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Antal gäster</Label>
                <Input type="number" min="1" value={form.party_size} onChange={e => setForm(f => ({ ...f, party_size: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Kanal</Label>
                <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-md h-10 text-sm px-2">
                  {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Ämne</Label>
              <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="t.ex. Catering studentfest" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Beskrivning</Label>
              <Textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={3}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
            </div>
            <Button onClick={create} disabled={saving || !form.customer_name.trim() || !(form.subject.trim() || form.body.trim())}
              className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Spara förfrågan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
